import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";

import MicNoneIcon from "@mui/icons-material/MicNone";
import SendIcon from "@mui/icons-material/Send";
import TextareaAutosize from "react-textarea-autosize";
import Modal from "@mui/material/Modal";
import AudioRecoder from "./AudioRecoder";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";

import "../../../../assets/css/chat/chat_textarea.css";
import {
  CHAT_ROOM_ATTACHMENTS_UPLOAD_URL,
  CHAT_ROOM_ATTACHMENTS_DELETE_URL,
} from "../../../../utils/api-ends";
import FileImg from "../../../../assets/media/file-img.png";
import Loader from "../../../../assets/media/loader.gif";
import useHttp from "../../../../hooks/useHttp";
import { sending_message_ui } from "../../../../store/slices/chatUi";

const access_token = JSON.parse(localStorage.getItem("auth_token"))?.access;

const TextareField = () => {
  const [open, setOpen] = useState(false);
  const { sendRequests } = useHttp();

  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [filePreviewSrcs, setFilePreviewSrcs] = useState([]);

  const dispatch = useDispatch();

  const chatSocket = useSelector((state) => state.chat.socket);
  const selectedRoom = useSelector((state) => state.chat.selectedRoom);

  const handleOpenAudioMic = () => setOpen(true);
  const handleCloseAudioMic = (e) => setOpen(false);

  const onMessageChange = (e) => setMessage(e.target.value);

  const handleSendMessage = () => {
    dispatch(sending_message_ui(true));

    let data = {
      command: "send_message",
      room_id: selectedRoom.channel_id,
    };

    if (!["", null, undefined].includes(message.trim())) {
      data["text_message"] = message;
      setMessage("");
    }
    if (attachments.length > 0) {
      data["attachment_links"] = attachments.map(
        (attachment) => attachment.fileLocation
      );
      setAttachments([]);
      setFilePreviewSrcs([]);
    }

    if (
      attachments.length === 0 &&
      ["", null, undefined].includes(message.trim())
    ) {
      return;
    }

    // Sends the message to the socket
    chatSocket.send(JSON.stringify(data));
  };

  const submitTextOrAttachmentsMessage = () => handleSendMessage();
  const sendVoiceMessage = (blob) => {
    // Sends the message to the socket
    chatSocket.send(
      JSON.stringify({
        command: "send_message",
        room_id: selectedRoom.channel_id,
        voice_file: blob,
      })
    );
  };

  const onFileChange = (e) => {
    if (e.target.files?.length > 0) {
      let listOfAxiosRequests = [];
      // Creates url for preview src
      const fileSrcs = Array.from(e.target.files).map((file) => {
        let uniqueId =
          Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15);

        // Which id is not exists in filePreviewSrcs
        while (filePreviewSrcs.includes(uniqueId)) {
          uniqueId =
            Math.random().toString(36).substring(2, 15) +
            Math.random().toString(36).substring(2, 15);
        }

        // Convert data to form data
        const formData = new FormData();
        formData.append("file", file);
        formData.append("unique_id", uniqueId);

        // Send request to server to upload file
        const cancelTokenSource = axios.CancelToken.source();
        let axiosRequests = axios({
          url: CHAT_ROOM_ATTACHMENTS_UPLOAD_URL(selectedRoom.channel_id).url,
          method: "post",
          data: formData,
          cancelToken: cancelTokenSource.token,
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${access_token}`,
          },
        });
        listOfAxiosRequests.push(axiosRequests);

        return {
          id: uniqueId,
          fileName: file.name,
          fileType: file.type,
          fileSrc: URL.createObjectURL(file),
          isLoading: true,
          axiosCancelToken: cancelTokenSource, // Cancel token for axios
        };
      });

      // Set file preview srcs
      setFilePreviewSrcs((prevState) => [...prevState, ...fileSrcs]);

      // Axios simultaneous requests
      axios
        .all(listOfAxiosRequests)
        .then(
          axios.spread((...response) => {
            response.forEach((res) => {
              // Stop loading one by one
              setFilePreviewSrcs((prevState) => {
                return prevState.map((file) => {
                  if (file.id === res.data?.data?.unique_id) {
                    file.isLoading = false;
                  }
                  return file;
                });
              });

              // Add file to attachments
              setAttachments((prevState) => [
                ...prevState,
                {
                  id: res.data?.data?.unique_id,
                  fileLocation: res.data?.data?.file_location,
                },
              ]);
            });
          })
        )
        .catch((errors) => {});
    }
  };

  const onFileRemove = (uniqueId) => {
    // If axios request is pending, cancel it
    filePreviewSrcs.forEach((file) => {
      if (file.id === uniqueId) {
        file.axiosCancelToken.cancel();
      }
    });

    // Remove file from attachments
    setFilePreviewSrcs((prevState) =>
      prevState.filter((file) => file.id !== uniqueId)
    );

    // Gets attachment path
    const attachmentPath = attachments.find(
      (attachment) => attachment.id === uniqueId
    );

    // Remove from server
    sendRequests(
      {
        url: CHAT_ROOM_ATTACHMENTS_DELETE_URL(selectedRoom.channel_id),
        method: "delete",
        data: {
          file_path: attachmentPath.fileLocation,
        },
      },
      () => {}
    );

    // Remove from already uploaded list
    setAttachments((prevState) =>
      prevState.filter((file) => file.id !== uniqueId)
    );
  };

  // Sends message by pressing enter
  const onEnterPress = (e) => {
    if (e.which === 13 && !e.shiftKey) {
      e.preventDefault();

      // Sends message
      handleSendMessage();
    }
  };

  return (
    <div className="chat_text_area">
      <div className="chat_text_box">
        <TextareaAutosize
          placeholder="Type you messages ..."
          value={message}
          onChange={onMessageChange}
          onKeyPress={onEnterPress}
        />

        <div
          className="text-area-message-box"
          sendable={
            filePreviewSrcs.length > 0 || message.trim() ? "true" : "false"
          }
        >
          <span
            className="message-box-send-icon voice-message-icon"
            onClick={handleOpenAudioMic}
          >
            <MicNoneIcon />
          </span>
          <span className="attachfile-input message-box-send-icon">
            <AttachFileIcon />
            <input
              type="file"
              multiple={true}
              onChange={onFileChange}
              accept="image/*, application/pdf"
            />
          </span>
          <span
            className="message-box-send-icon sending-message"
            onClick={submitTextOrAttachmentsMessage}
          >
            <SendIcon />
          </span>
        </div>
      </div>

      <Modal
        className="audio-re-modal"
        open={open}
        onClose={handleCloseAudioMic}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <AudioRecoder
          className="audio-re-paper"
          submitAudio={sendVoiceMessage}
          modalClose={handleCloseAudioMic}
        />
      </Modal>

      {filePreviewSrcs.length > 0 && (
        <div className="preview-attachments">
          {filePreviewSrcs.map((attachment, index) => (
            <div key={index} className="preview-attachment-files">
              <CancelOutlinedIcon
                className="preview-attachment-remove"
                onClick={() => onFileRemove(attachment.id)}
              />

              {attachment.isLoading && (
                <img
                  className="preview-attachment-loader"
                  src={Loader}
                  alt="loader"
                />
              )}
              {attachment.fileType.includes("image") ? (
                <img
                  style={{ width: "70px", height: "70px" }}
                  src={attachment.fileSrc}
                  alt="preview"
                />
              ) : (
                <>
                  <img
                    style={{ width: "55px", height: "70px" }}
                    src={FileImg}
                    alt="preview"
                  />
                  {/* <p>{attachment.fileName}</p> */}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TextareField;
